import {Checkbox, IconButton, Table, useFormContext} from 'platform/components';
import {Box, HStack, Heading, Scroll, getSize} from 'platform/foundation';

import {memo, useState} from 'react';
import {useWatch} from 'react-hook-form';

import {isNil, isNotEmpty, pipe} from 'ramda';
import {isTrue} from 'ramda-adjunct';

import {ListProtectedUnitsApiResponse, useGetRoleQuery} from '@dms/api/security';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';

import {Nullish, useQueryState} from 'shared/web';

import {ResourceItem} from './components/ResourceItem';
import {collectProtectedUnitIds} from './utils/collectProtectedUnitIds';
import {filterAndMapResourcesBySearchString} from './utils/filterAndMapResourcesBySearchString';
import {filterResourcesByFeatureFlag} from './utils/filterResourcesByFeatureFlag';

interface AccessControlListParams {
  roleId: string;
  searchString: string | null;
  resources: ListProtectedUnitsApiResponse | undefined;
}

const HEADER_HEIGHT = getSize(37);
const FORM_HEIGHT = getSize(44);
const FOOTER_HEIGHT = getSize(35);

interface ExpandState {
  defaultOpen: boolean;
  overrides: Map<string, boolean>;
}

function AccessControlListComponent(props: AccessControlListParams) {
  const {data: role} = useGetRoleQuery({roleId: props.roleId});

  const {control, setValue, formState} = useFormContext();
  const protectedUnits: Record<string, {value: boolean | Nullish} | undefined> = useWatch({
    control,
    name: 'protectedUnits',
  });

  const [mode] = useQueryState('mode');
  const [expandState, setExpandState] = useState<ExpandState>(() => ({
    defaultOpen: isNil(mode),
    overrides: new Map(),
  }));

  const isSectionOpen = (sectionId: string) =>
    expandState.overrides.get(sectionId) ?? expandState.defaultOpen;

  const toggleSection = (sectionId: string) => {
    setExpandState((prev) => {
      const overrides = new Map(prev.overrides);
      overrides.set(sectionId, !(overrides.get(sectionId) ?? prev.defaultOpen));
      return {...prev, overrides};
    });
  };

  const hasCollapsedSection = Array.from(expandState.overrides.values()).some(
    (isExpanded) => !isExpanded
  );
  const areAllSectionsOpen = expandState.defaultOpen && !hasCollapsedSection;

  const handleToggleAllSections = () => {
    setExpandState(
      areAllSectionsOpen
        ? {defaultOpen: false, overrides: new Map()}
        : {defaultOpen: true, overrides: new Map()}
    );
  };

  const isSystem = role?.system ?? false;

  const resources = pipe(
    filterAndMapResourcesBySearchString(props.searchString),
    filterResourcesByFeatureFlag
  )(props.resources);

  // Only the units the filtered tree actually renders may be bulk toggled or counted: the form
  // also carries flag-hidden and search-hidden units, and granting those from here would create
  // permissions that cannot be reviewed or revoked in the editor
  const visibleProtectedUnitIds = collectProtectedUnitIds(resources);
  const visibleProtectedUnitValues = visibleProtectedUnitIds.map(
    (id) => protectedUnits?.[id]?.value
  );

  const isChecked =
    isNotEmpty(visibleProtectedUnitValues) && visibleProtectedUnitValues.every(isTrue);
  const isIndeterminate = !isChecked && visibleProtectedUnitValues.some(isTrue);

  const handleToggleAllPermissions = (value: boolean) => {
    visibleProtectedUnitIds.forEach((id) => setValue(`protectedUnits.${id}.value`, value));
  };

  return (
    <Scroll height={`calc(100vh - ${HEADER_HEIGHT} - ${FORM_HEIGHT} - ${FOOTER_HEIGHT})`} always>
      <Table
        data-testid={testIds.settings.roleManagementDetail('aclTable')}
        columns={[
          {
            element: (
              <Box paddingHorizontal={1} paddingVertical={4}>
                <HStack align="center" spacing={21}>
                  <HStack align="center" spacing={8}>
                    <IconButton
                      icon={
                        areAllSectionsOpen
                          ? 'hardware/keyboard_arrow_up'
                          : 'hardware/keyboard_arrow_down'
                      }
                      onClick={handleToggleAllSections}
                      data-testid={testIds.settings.roleManagementDetail('toggle-all-sections')}
                    />
                    <Checkbox
                      value={isChecked}
                      isIndeterminate={isIndeterminate}
                      onChange={handleToggleAllPermissions}
                      isDisabled={isSystem || formState.isSubmitting}
                      data-testid={testIds.settings.roleManagementDetail('select-all')}
                    />
                  </HStack>
                  <Box paddingHorizontal={2} borderLeft="solid 1px" borderColor="general.separator">
                    <Heading color="secondary" size={5}>
                      {i18n.t('page.settings.role.tabs.label.permissions')}
                    </Heading>
                  </Box>
                </HStack>
              </Box>
            ),
            width: '40%',
          },
          {
            element: (
              <Box paddingHorizontal={2} borderLeft="solid 1px" borderColor="general.separator">
                <HStack>
                  <Heading color="secondary" size={5}>
                    {i18n.t('general.labels.description')}
                  </Heading>
                </HStack>
              </Box>
            ),
            width: '30%',
          },
          {
            element: (
              <Box paddingHorizontal={2} borderLeft="solid 1px" borderColor="general.separator">
                <HStack>
                  <Heading color="secondary" size={5}>
                    {i18n.t('acl.permission.condition')}
                  </Heading>
                </HStack>
              </Box>
            ),
            width: '30%',
          },
        ]}
      >
        {resources?.map((resource) => (
          <ResourceItem
            data={resource}
            isSectionOpen={isSectionOpen}
            toggleSection={toggleSection}
            data-testid={testIds.settings.roleManagementDetail('aclTable')}
            key={resource.id}
          />
        ))}
      </Table>
    </Scroll>
  );
}

// memo is needed to prevent re-renders when non-debounced search string in parent changes
export const AccessControlList = memo(AccessControlListComponent);
